/**
 * Locale helper — single source of truth for country-specific strings.
 *
 * Use everywhere you render a price, an energy label, a postcode prompt,
 * or a regulator badge. Avoids the 7-files-need-updating problem we hit
 * when the UK listings shipped with €/BER/Eircode all over the Property
 * Passport.
 *
 * Usage::
 *
 *     import { localeForCountry, fmtMoney } from '../utils/locale';
 *     const L = localeForCountry(property.location?.country);
 *     // L.currencySymbol === '£' for UK, '€' for IE
 *     // L.energyLabel    === 'EPC'   vs  'BER'
 *     // L.postcodeLabel  === 'Postcode' vs 'Eircode'
 *     // fmtMoney(525000, 'United Kingdom')  → '£525,000'
 */

const PROFILES = {
  IE: {
    code: 'IE',
    locale: 'en-IE',
    currency: 'EUR',
    currencySymbol: '€',
    energyLabel: 'BER',
    energyRegisterName: 'SEAI BER register',
    postcodeLabel: 'Eircode',
    postcodeRegex: '^[A-Z]\\d{2} ?[A-Z0-9]{4}$',
    regulator: 'PSRA',
    soldPricesSource: 'Property Price Register',
  },
  UK: {
    code: 'UK',
    locale: 'en-GB',
    currency: 'GBP',
    currencySymbol: '£',
    energyLabel: 'EPC',
    energyRegisterName: 'MHCLG EPC register',
    postcodeLabel: 'Postcode',
    postcodeRegex: '^[A-Z]{1,2}\\d{1,2}[A-Z]? ?\\d[A-Z]{2}$',
    regulator: 'Propertymark',
    soldPricesSource: 'HM Land Registry',
  },
};

const UK_NAMES = new Set([
  'UK', 'GB', 'United Kingdom', 'Great Britain',
  'England', 'Scotland', 'Wales', 'Northern Ireland',
]);

export const localeForCountry = (country) => {
  if (country && UK_NAMES.has(String(country))) return PROFILES.UK;
  return PROFILES.IE;
};

export const fmtMoney = (value, country) => {
  const L = localeForCountry(country);
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return `${L.currencySymbol}0`;
  }
  return new Intl.NumberFormat(L.locale, {
    style: 'currency',
    currency: L.currency,
    maximumFractionDigits: 0,
  }).format(Number(value));
};
